import { useCallback, useEffect, useRef, useState } from 'react';

import qs from 'qs';
import { useHistory, useLocation } from 'react-router';

import useQuery from '~/hooks/useQuery';

import { parseSearchString } from '~/utils/url';

export interface PromoRequestUrlState {
  codeName?: string;
  voucherName?: string;
  userId?: string;
  errorCode?: string;
  requestSource?: string;
  page?: number;
  limit?: number;
  dev?: boolean;
  // Accordion states for all sections
  expanded?: boolean;
  itemsExpanded?: boolean;
  loyaltyItemsExpanded?: boolean;
  voucherLogsExpanded?: boolean;
  logsExpanded?: boolean;
  failuresExpanded?: boolean;
  usersExpanded?: boolean;
  // Pagination for different tables
  logPage?: number;
  logLimit?: number;
  includeDeleted?: boolean;
  failurePage?: number;
  failureLimit?: number;
  userPage?: number;
  userLimit?: number;
  // Date filters for logs
  logAfter?: string;
  logBefore?: string;
}

export interface UsePromoRequestUrlProps {
  brand: string;
  handleUserIdFilterSet?: (userId: string) => void;
}

export interface UsePromoRequestUrlReturn {
  urlState: PromoRequestUrlState;
  updateUrlState: (newState: Partial<PromoRequestUrlState>) => void;
  isDevMode: boolean;
  setIsDevMode: (isDev: boolean) => void;
  // Accordion state management
  isExpanded: boolean;
  setIsExpanded: (expanded: boolean) => void;
  isItemsExpanded: boolean;
  setIsItemsExpanded: (expanded: boolean) => void;
  isLoyaltyItemsExpanded: boolean;
  setIsLoyaltyItemsExpanded: (expanded: boolean) => void;
  isVoucherLogsExpanded: boolean;
  setIsVoucherLogsExpanded: (expanded: boolean) => void;
  isLogsExpanded: boolean;
  setIsLogsExpanded: (expanded: boolean) => void;
  isFailuresExpanded: boolean;
  setIsFailuresExpanded: (expanded: boolean) => void;
  isUsersExpanded: boolean;
  setIsUsersExpanded: (expanded: boolean) => void;
  // Pagination update methods
  updateLogPagination: (page: number, limit: number) => void;
  updateFailurePagination: (page: number, limit: number) => void;
  updateUserPagination: (page: number, limit: number) => void;
  // Date filter update methods
  updateLogDateFilters: (logAfter?: string, logBefore?: string) => void;
  updateIncludeDeleted: (includeDeleted: boolean) => void;
}

export default function usePromoRequestUrl({
  brand,
  handleUserIdFilterSet,
}: UsePromoRequestUrlProps): UsePromoRequestUrlReturn {
  const history = useHistory();
  const location = useLocation();
  const query = useQuery();
  const debounceTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Parse URL parameters
  const [urlState, setUrlState] = useState<PromoRequestUrlState>(() => ({
    codeName: query.get('codeName') || undefined,
    voucherName: query.get('voucherName') || undefined,
    userId: query.get('userId') || undefined,
    errorCode: query.get('errorCode') || undefined,
    requestSource: query.get('requestSource') || undefined,
    page: query.get('page') ? parseInt(query.get('page')!, 10) : undefined,
    limit: query.get('limit') ? parseInt(query.get('limit')!, 10) : undefined,
    dev: query.get('dev') === 'true',
    // Accordion states for all sections
    expanded: query.get('expanded') === 'true',
    itemsExpanded: query.get('itemsExpanded') === 'true',
    logsExpanded: query.get('logsExpanded') === 'true',
    failuresExpanded: query.get('failuresExpanded') === 'true',
    usersExpanded: query.get('usersExpanded') === 'true',
    loyaltyItemsExpanded: query.get('loyaltyItemsExpanded') === 'true',
    voucherLogsExpanded: query.get('voucherLogsExpanded') === 'true',
    // Pagination for different tables
    logPage: query.get('logPage') ? parseInt(query.get('logPage')!, 10) : undefined,
    logLimit: query.get('logLimit') ? parseInt(query.get('logLimit')!, 10) : undefined,
    failurePage: query.get('failurePage') ? parseInt(query.get('failurePage')!, 10) : undefined,
    failureLimit: query.get('failureLimit') ? parseInt(query.get('failureLimit')!, 10) : undefined,
    userPage: query.get('userPage') ? parseInt(query.get('userPage')!, 10) : undefined,
    userLimit: query.get('userLimit') ? parseInt(query.get('userLimit')!, 10) : undefined,
    // Date filters for logs
    logAfter: query.get('logAfter') || undefined,
    logBefore: query.get('logBefore') || undefined,
    includeDeleted: query.get('includeDeleted') === 'true',
  }));

  const [isDevMode, setIsDevModeState] = useState(!!query.get('dev'));
  const [isExpanded, setIsExpandedState] = useState(query.get('expanded') === 'true');
  const [isItemsExpanded, setIsItemsExpandedState] = useState(query.get('itemsExpanded') === 'true');
  const [isLogsExpanded, setIsLogsExpandedState] = useState(query.get('logsExpanded') === 'true');
  const [isFailuresExpanded, setIsFailuresExpandedState] = useState(query.get('failuresExpanded') === 'true');
  const [isUsersExpanded, setIsUsersExpandedState] = useState(query.get('usersExpanded') === 'true');
  const [isLoyaltyItemsExpanded, setIsLoyaltyItemsExpandedState] = useState(
    query.get('loyaltyItemsExpanded') === 'true',
  );
  const [isVoucherLogsExpanded, setIsVoucherLogsExpandedState] = useState(query.get('voucherLogsExpanded') === 'true');
  // Debounced URL update function
  const debouncedUpdateUrl = useCallback(
    (updatedState: PromoRequestUrlState) => {
      // Clear existing timeout
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }

      // Set new timeout for URL update
      debounceTimeoutRef.current = setTimeout(() => {
        // Merge into the current query: defined values set/replace params; undefined removes them
        // (filtering undefined then addQuery-merge would leave stale keys in the URL).
        const queryObj: Record<string, unknown> = { ...parseSearchString(location.search) };
        for (const [key, value] of Object.entries(updatedState)) {
          if (value === undefined) {
            delete queryObj[key];
          } else {
            queryObj[key] = value;
          }
        }
        const newLocation = {
          ...location,
          search: `?${qs.stringify(queryObj)}`,
        };
        history.push(newLocation);
      }, 300); // 300ms debounce delay
    },
    [location, history],
  );

  // Update URL and state
  const updateUrlState = useCallback(
    (newState: Partial<PromoRequestUrlState>) => {
      const updatedState = { ...urlState, ...newState };
      setUrlState(updatedState);

      // Debounce the URL update
      debouncedUpdateUrl(updatedState);

      // Handle userId filter callback immediately (not debounced)
      if (newState.userId !== undefined && handleUserIdFilterSet) {
        handleUserIdFilterSet(newState.userId);
      }
    },
    [urlState, debouncedUpdateUrl, handleUserIdFilterSet],
  );

  // Update dev mode
  const setIsDevMode = useCallback(
    (isDev: boolean) => {
      setIsDevModeState(isDev);
      updateUrlState({ dev: isDev });
    },
    [updateUrlState],
  );

  // Update expanded state
  const setIsExpanded = useCallback(
    (expanded: boolean) => {
      setIsExpandedState(expanded);
      updateUrlState({ expanded });
    },
    [updateUrlState],
  );

  // Update items expanded state
  const setIsItemsExpanded = useCallback(
    (expanded: boolean) => {
      setIsItemsExpandedState(expanded);
      updateUrlState({ itemsExpanded: expanded });
    },
    [updateUrlState],
  );

  // Update logs expanded state
  const setIsLogsExpanded = useCallback(
    (expanded: boolean) => {
      setIsLogsExpandedState(expanded);
      updateUrlState({ logsExpanded: expanded });
    },
    [updateUrlState],
  );

  // Update failures expanded state
  const setIsFailuresExpanded = useCallback(
    (expanded: boolean) => {
      setIsFailuresExpandedState(expanded);
      updateUrlState({ failuresExpanded: expanded });
    },
    [updateUrlState],
  );

  // Update users expanded state
  const setIsUsersExpanded = useCallback(
    (expanded: boolean) => {
      setIsUsersExpandedState(expanded);
      updateUrlState({ usersExpanded: expanded });
    },
    [updateUrlState],
  );

  // Update loyalty items expanded state
  const setIsLoyaltyItemsExpanded = useCallback(
    (expanded: boolean) => {
      setIsLoyaltyItemsExpandedState(expanded);
      updateUrlState({ loyaltyItemsExpanded: expanded });
    },
    [updateUrlState],
  );

  const setIsVoucherLogsExpanded = useCallback(
    (expanded: boolean) => {
      setIsVoucherLogsExpandedState(expanded);
      updateUrlState({ voucherLogsExpanded: expanded });
    },
    [updateUrlState],
  );
  // Pagination update methods
  const updateLogPagination = useCallback(
    (page: number, limit: number) => {
      updateUrlState({ logPage: page, logLimit: limit });
    },
    [updateUrlState],
  );

  const updateFailurePagination = useCallback(
    (page: number, limit: number) => {
      updateUrlState({ failurePage: page, failureLimit: limit });
    },
    [updateUrlState],
  );

  const updateUserPagination = useCallback(
    (page: number, limit: number) => {
      updateUrlState({ userPage: page, userLimit: limit });
    },
    [updateUrlState],
  );

  // Date filter update methods
  const updateLogDateFilters = useCallback(
    (logAfter?: string, logBefore?: string) => {
      updateUrlState({ logAfter, logBefore });
    },
    [updateUrlState],
  );

  const updateIncludeDeleted = useCallback(
    (includeDeleted: boolean) => {
      updateUrlState({ includeDeleted });
    },
    [updateUrlState],
  );

  // Sync URL state when query parameters change
  useEffect(() => {
    const newUrlState: PromoRequestUrlState = {
      codeName: query.get('codeName') || undefined,
      voucherName: query.get('voucherName') || undefined,
      userId: query.get('userId') || undefined,
      errorCode: query.get('errorCode') || undefined,
      requestSource: query.get('requestSource') || undefined,
      page: query.get('page') ? parseInt(query.get('page')!, 10) : undefined,
      limit: query.get('limit') ? parseInt(query.get('limit')!, 10) : undefined,
      dev: query.get('dev') === 'true',
      // Accordion states for all sections
      expanded: query.get('expanded') === 'true',
      itemsExpanded: query.get('itemsExpanded') === 'true',
      logsExpanded: query.get('logsExpanded') === 'true',
      failuresExpanded: query.get('failuresExpanded') === 'true',
      usersExpanded: query.get('usersExpanded') === 'true',
      voucherLogsExpanded: query.get('voucherLogsExpanded') === 'true',
      // Pagination for different tables
      logPage: query.get('logPage') ? parseInt(query.get('logPage')!, 10) : undefined,
      logLimit: query.get('logLimit') ? parseInt(query.get('logLimit')!, 10) : undefined,
      failurePage: query.get('failurePage') ? parseInt(query.get('failurePage')!, 10) : undefined,
      failureLimit: query.get('failureLimit') ? parseInt(query.get('failureLimit')!, 10) : undefined,
      userPage: query.get('userPage') ? parseInt(query.get('userPage')!, 10) : undefined,
      userLimit: query.get('userLimit') ? parseInt(query.get('userLimit')!, 10) : undefined,
      // Date filters for logs
      logAfter: query.get('logAfter') || undefined,
      logBefore: query.get('logBefore') || undefined,
      includeDeleted: query.get('includeDeleted') === 'true',
    };

    // Only update state if there are actual changes to avoid unnecessary re-renders
    const hasChanges = JSON.stringify(newUrlState) !== JSON.stringify(urlState);

    if (hasChanges) {
      setUrlState(newUrlState);
      setIsDevModeState(!!query.get('dev'));
      setIsExpandedState(query.get('expanded') === 'true');
      setIsItemsExpandedState(query.get('itemsExpanded') === 'true');
      setIsLogsExpandedState(query.get('logsExpanded') === 'true');
      setIsFailuresExpandedState(query.get('failuresExpanded') === 'true');
      setIsUsersExpandedState(query.get('usersExpanded') === 'true');
      setIsVoucherLogsExpandedState(query.get('voucherLogsExpanded') === 'true');

      // Handle userId filter callback on URL change
      if (newUrlState.userId && handleUserIdFilterSet) {
        handleUserIdFilterSet(newUrlState.userId);
      }
    }
  }, [query, handleUserIdFilterSet, urlState]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, []);

  return {
    urlState,
    updateUrlState,
    isDevMode,
    setIsDevMode,
    // Accordion state management
    isExpanded,
    setIsExpanded,
    isItemsExpanded,
    isLoyaltyItemsExpanded,
    setIsLoyaltyItemsExpanded,
    isVoucherLogsExpanded,
    setIsVoucherLogsExpanded,
    setIsItemsExpanded,
    isLogsExpanded,
    setIsLogsExpanded,
    isFailuresExpanded,
    setIsFailuresExpanded,
    isUsersExpanded,
    setIsUsersExpanded,
    // Pagination update methods
    updateLogPagination,
    updateFailurePagination,
    updateUserPagination,
    // Date filter update methods
    updateLogDateFilters,
    updateIncludeDeleted,
  };
}
